import { makeStyles } from "@mui/styles";
import { Box, Button, Card, Link, Stack, Typography } from "@mui/material";
import { ReactComponent as StarIcon } from "../../../assets/MiniProfileStarIcon.svg";
import { DbUser, UniversalModalCtx, UserCtx } from "../../../interfaces";
import { ClassSelector } from "./ClassSelector";
import { NotificationType, Role } from "../../../enums";
import { generateGtmEvent } from "../../../Helpers";
import { UniversalModalContext } from "../../../context/UniversalModalContext";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import Axios from "../../../services/axios";
import { UserNotification } from "@backend-types";

const padY = 48;
const triangleLength = 16;
const tooltipWidth = 364;
const tooltipHeight = 178;

const useStyle = makeStyles((theme) => ({
  profileCircle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "120px",
    height: "120px",
    backgroundColor: "#E2F7FF",
    borderRadius: "50%",
    margin: "0 auto",
    position: "relative",
  },
  initials: {
    fontSize: "3em",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },

  tooltip: (props: any) => ({
    backgroundColor: theme.palette.text.primary,
    color: "white",
    borderRadius: 15,
    padding: "1.5rem",
    width: tooltipWidth,
    height: props.tooltipHeight || tooltipHeight,
    boxSizing: "border-box",
    boxShadow: `inset 0px 0px 0px 2px ${theme.palette.divider}`,
    zIndex: 3500,
  }),
  outlinedButton: {
    backgroundColor: "transparent",
    borderColor: "white",
    color: "white",
    fontSize: 16,
    padding: "8px 12px",
    borderRadius: "16px",
    height: "29px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderColor: "white",
      color: "white",
    },
  },
  triangle: {},
  triangleright: {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    width: triangleLength,
    height: triangleLength,
    backgroundColor: theme.palette.text.primary,
    boxShadow: `inset -4px 4px 0px -2px ${theme.palette.divider}`,
    transform: "translateY(-50%) translateX(-50%) rotate(45deg)",
    zIndex: 0,
  },
  header: {
    color: "white",
    fontSize: 17,
    fontWeight: 600,
    marginBottom: "0.6rem",
  },
  body: {
    color: "white",
    fontSize: 16,
    fontWeight: 400,
  },
}));

interface MiniProfileCardProps {
  currentUser: DbUser;
  fullName: string;
  initials: string;
  school?: string;
  course?: string;
}

export const MiniProfileCard = (props: MiniProfileCardProps) => {
  const classes = useStyle(props);
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  const [userNotification, setUserNotifications] = useState<UserNotification[]>(
    userCtx.dbUser.user_notifications || []
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleOpenTooltip = () => {
    setTooltipOpen(true);
  };

  const handleCloseTooltip = async () => {
    setTooltipOpen(false);
    let response = await Axios.patch("notifications");
    userCtx.setDbUser({
      ...userCtx.dbUser,
      user_notifications: response.data.results,
    });
  };

  useEffect(() => {
    if (userCtx.dbUser.user_notifications) {
      setUserNotifications(userCtx.dbUser.user_notifications ?? []);
    }
  }, [userCtx.dbUser.user_notifications]);

  useEffect(() => {
    if (userNotification.length > 0) {
      const showToolTip = userNotification.filter((notification) => {
        return (
          notification.type === NotificationType.New_Course_Enrolled &&
          !notification.is_read
        );
      });
      if (showToolTip.length > 0) {
        handleOpenTooltip();
      } else {
      }
    }
  }, [userNotification]);

  return (
    <>
      <Card
        sx={{
          marginBottom: "24px",
          padding: "2rem",
          overflow: "visible",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}>
          <Box className={classes.profileCircle}>
            <Typography
              component="span"
              variant="h1"
              data-sentry-mask
              className={classes.initials}>
              {props.initials}
            </Typography>
            <StarIcon style={{ width: 186, height: 116, position: "absolute" }} />
          </Box>
          <Typography
            sx={{
              mb: 1.5,
              marginTop: "24px",
              marginBottom: "24px",
              textAlign: "center",
            }}
            variant="h3"
            data-sentry-mask
            component="div">
            {props.fullName}
          </Typography>
          {props.school && (
            <Typography variant="body2" sx={{ marginBottom: "0.75rem" }} data-sentry-mask>
              {props.school}
            </Typography>
          )}
          {props.currentUser.role === Role.student ? (
            <Box sx={{ position: "relative" }}>
              <ClassSelector />
              {tooltipOpen && (
                <Box
                  className={classes.tooltip}
                  sx={{
                    position: "absolute",
                    left: `-${tooltipWidth + triangleLength}px`,
                    top: "-31px",
                    transition: "all 1s ease",
                    zIndex: 1000,
                  }}>
                  <Box
                    className={classes.triangle}
                    sx={{
                      position: "absolute",
                      left: tooltipWidth,
                      top: 0 + padY,
                      zIndex: 1201,
                    }}>
                    <div className={classes.triangleright}></div>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}>
                    <Box>
                      <Typography className={classes.header}>Switch courses</Typography>
                      <Typography className={classes.body}>
                        You were recently added to a new course! Use the course selector
                        in your dashboard to switch between courses.
                      </Typography>
                    </Box>
                    <Box sx={{ marginTop: "auto" }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={0}>
                        <Button
                          sx={{ marginLeft: "auto" }}
                          className={classes.outlinedButton}
                          variant="outlined"
                          size="small"
                          onClick={handleCloseTooltip}
                          aria-label="Got it">
                          Got it
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Link
              onClick={() => {
                generateGtmEvent({
                  event: "modal",
                  action: "open",
                  name: "onclick",
                  type: "click",
                  region: "main content",
                  section: `${props.fullName}`,
                  text: "edit account and system settings",
                  component: "home",
                });
                universalModalCtx.setUserAccountModalOpen(true);
              }}>
              <Typography sx={{ textAlign: "center" }}>
                Edit account and system settings
              </Typography>
            </Link>
          )}
        </Box>
      </Card>
    </>
  );
};
